/**
 * adminTaxonomiesSlice — Redux slice for admin taxonomy + node write
 * operations (CON-CAT-001).
 *
 * State shape
 * -----------
 * ```ts
 * {
 *   selectedTaxonomyIds: number[];
 *   mutationStatus: 'idle' | 'pending' | 'success' | 'error';
 *   mutationError: ApiError | null;
 *   importStatus: 'idle' | 'pending' | 'success' | 'error';
 *   importError: ApiError | null;
 *   importReport: TaxonomyImportReport | null;
 * }
 * ```
 *
 * Why a slice alongside TanStack Query
 * ------------------------------------
 * TanStack Query owns all server-state reads (list, detail, node list).
 * The slice tracks UI state that crosses query/route boundaries:
 *   - `selectedTaxonomyIds` — checked rows in the list DataTable.
 *   - `mutationStatus / mutationError` — last write attempt for toast +
 *     form-level error banners.
 *   - `importStatus / importError / importReport` — upload + parse progress.
 *
 * Per `.claude/rules/frontend-data-flow.md` §1, capsules NEVER import
 * this slice; only the view-level containers do.
 */
import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit';

import {
  createAdminTaxonomy,
  createAdminTaxonomyNode,
  deleteAdminTaxonomy,
  deactivateAdminTaxonomyNode,
  importAdminTaxonomyNodes,
  updateAdminTaxonomy,
  updateAdminTaxonomyNode,
} from '@dfq/shared/api/admin/taxonomies';
import { ApiError } from '@dfq/shared/api/_envelope';
import type {
  Taxonomy,
  TaxonomyImportReport,
  TaxonomyNode,
  TaxonomyNodeWritePayload,
  TaxonomyWritePayload,
} from '@dfq/shared/types/taxonomy';

// ---------------------------------------------------------------------------
// State
// ---------------------------------------------------------------------------

export type AdminTaxonomyMutationStatus = 'idle' | 'pending' | 'success' | 'error';

export interface AdminTaxonomiesState {
  selectedTaxonomyIds: number[];
  mutationStatus: AdminTaxonomyMutationStatus;
  mutationError: ApiError | null;
  importStatus: AdminTaxonomyMutationStatus;
  importError: ApiError | null;
  importReport: TaxonomyImportReport | null;
}

const initialState: AdminTaxonomiesState = {
  selectedTaxonomyIds: [],
  mutationStatus: 'idle',
  mutationError: null,
  importStatus: 'idle',
  importError: null,
  importReport: null,
};

// ---------------------------------------------------------------------------
// Thunk helpers
// ---------------------------------------------------------------------------

type ThunkApiError = { rejectValue: ApiError };

// ---------------------------------------------------------------------------
// Taxonomy CRUD thunks
// ---------------------------------------------------------------------------

export interface CreateTaxonomyArg {
  payload: TaxonomyWritePayload;
}

export const createTaxonomyThunk = createAsyncThunk<
  Taxonomy,
  CreateTaxonomyArg,
  ThunkApiError
>('adminTaxonomies/create', async ({ payload }, { rejectWithValue }) => {
  try {
    return await createAdminTaxonomy(payload);
  } catch (error) {
    if (error instanceof ApiError) return rejectWithValue(error);
    throw error;
  }
});

export interface UpdateTaxonomyArg {
  slug: string;
  payload: Partial<TaxonomyWritePayload>;
}

export const updateTaxonomyThunk = createAsyncThunk<
  Taxonomy,
  UpdateTaxonomyArg,
  ThunkApiError
>('adminTaxonomies/update', async ({ slug, payload }, { rejectWithValue }) => {
  try {
    return await updateAdminTaxonomy(slug, payload);
  } catch (error) {
    if (error instanceof ApiError) return rejectWithValue(error);
    throw error;
  }
});

export interface DeleteTaxonomyArg {
  slug: string;
}

export const deleteTaxonomyThunk = createAsyncThunk<
  string,
  DeleteTaxonomyArg,
  ThunkApiError
>('adminTaxonomies/delete', async ({ slug }, { rejectWithValue }) => {
  try {
    await deleteAdminTaxonomy(slug);
    return slug;
  } catch (error) {
    if (error instanceof ApiError) return rejectWithValue(error);
    throw error;
  }
});

// ---------------------------------------------------------------------------
// Taxonomy node CRUD thunks
// ---------------------------------------------------------------------------

export interface CreateTaxonomyNodeArg {
  slug: string;
  payload: TaxonomyNodeWritePayload;
}

export const createTaxonomyNodeThunk = createAsyncThunk<
  TaxonomyNode,
  CreateTaxonomyNodeArg,
  ThunkApiError
>('adminTaxonomies/createNode', async ({ slug, payload }, { rejectWithValue }) => {
  try {
    return await createAdminTaxonomyNode(slug, payload);
  } catch (error) {
    if (error instanceof ApiError) return rejectWithValue(error);
    throw error;
  }
});

export interface UpdateTaxonomyNodeArg {
  slug: string;
  nodeId: number;
  payload: Partial<TaxonomyNodeWritePayload>;
}

export const updateTaxonomyNodeThunk = createAsyncThunk<
  TaxonomyNode,
  UpdateTaxonomyNodeArg,
  ThunkApiError
>('adminTaxonomies/updateNode', async ({ slug, nodeId, payload }, { rejectWithValue }) => {
  try {
    return await updateAdminTaxonomyNode(slug, nodeId, payload);
  } catch (error) {
    if (error instanceof ApiError) return rejectWithValue(error);
    throw error;
  }
});

export interface DeleteTaxonomyNodeArg {
  slug: string;
  nodeId: number;
}

export const deactivateTaxonomyNodeThunk = createAsyncThunk<
  number,
  DeleteTaxonomyNodeArg,
  ThunkApiError
>('adminTaxonomies/deleteNode', async ({ slug, nodeId }, { rejectWithValue }) => {
  try {
    await deactivateAdminTaxonomyNode(slug, nodeId);
    return nodeId;
  } catch (error) {
    if (error instanceof ApiError) return rejectWithValue(error);
    throw error;
  }
});

// ---------------------------------------------------------------------------
// Import thunk
// ---------------------------------------------------------------------------

export interface ImportTaxonomyNodesArg {
  slug: string;
  file: File;
}

export const importTaxonomyNodesThunk = createAsyncThunk<
  TaxonomyImportReport,
  ImportTaxonomyNodesArg,
  ThunkApiError
>('adminTaxonomies/import', async ({ slug, file }, { rejectWithValue }) => {
  try {
    return await importAdminTaxonomyNodes(slug, file);
  } catch (error) {
    if (error instanceof ApiError) return rejectWithValue(error);
    throw error;
  }
});

// ---------------------------------------------------------------------------
// Slice
// ---------------------------------------------------------------------------

export const adminTaxonomiesSlice = createSlice({
  name: 'adminTaxonomies',
  initialState,
  reducers: {
    setSelectedTaxonomyIds(state, action: PayloadAction<number[]>) {
      state.selectedTaxonomyIds = action.payload;
    },
    clearSelection(state) {
      state.selectedTaxonomyIds = [];
    },
    resetMutation(state) {
      state.mutationStatus = 'idle';
      state.mutationError = null;
    },
    resetImport(state) {
      state.importStatus = 'idle';
      state.importError = null;
      state.importReport = null;
    },
  },
  extraReducers: (builder) => {
    const setPending = (state: AdminTaxonomiesState) => {
      state.mutationStatus = 'pending';
      state.mutationError = null;
    };
    const setSuccess = (state: AdminTaxonomiesState) => {
      state.mutationStatus = 'success';
      state.mutationError = null;
    };
    const setError = (
      state: AdminTaxonomiesState,
      action: { payload?: ApiError; error?: { message?: string } },
    ) => {
      state.mutationStatus = 'error';
      state.mutationError =
        action.payload instanceof ApiError
          ? action.payload
          : new ApiError({
              message: action.error?.message ?? 'Taxonomy operation failed',
              status: 0,
            });
    };

    // Taxonomy CRUD
    builder
      .addCase(createTaxonomyThunk.pending, setPending)
      .addCase(createTaxonomyThunk.fulfilled, setSuccess)
      .addCase(createTaxonomyThunk.rejected, setError)

      .addCase(updateTaxonomyThunk.pending, setPending)
      .addCase(updateTaxonomyThunk.fulfilled, setSuccess)
      .addCase(updateTaxonomyThunk.rejected, setError)

      .addCase(deleteTaxonomyThunk.pending, setPending)
      .addCase(deleteTaxonomyThunk.fulfilled, setSuccess)
      .addCase(deleteTaxonomyThunk.rejected, setError)

      // Node CRUD
      .addCase(createTaxonomyNodeThunk.pending, setPending)
      .addCase(createTaxonomyNodeThunk.fulfilled, setSuccess)
      .addCase(createTaxonomyNodeThunk.rejected, setError)

      .addCase(updateTaxonomyNodeThunk.pending, setPending)
      .addCase(updateTaxonomyNodeThunk.fulfilled, setSuccess)
      .addCase(updateTaxonomyNodeThunk.rejected, setError)

      .addCase(deactivateTaxonomyNodeThunk.pending, setPending)
      .addCase(deactivateTaxonomyNodeThunk.fulfilled, setSuccess)
      .addCase(deactivateTaxonomyNodeThunk.rejected, setError)

      // Import
      .addCase(importTaxonomyNodesThunk.pending, (state) => {
        state.importStatus = 'pending';
        state.importError = null;
        state.importReport = null;
      })
      .addCase(importTaxonomyNodesThunk.fulfilled, (state, action) => {
        state.importStatus = 'success';
        state.importError = null;
        state.importReport = action.payload;
      })
      .addCase(importTaxonomyNodesThunk.rejected, (state, action) => {
        state.importStatus = 'error';
        state.importError =
          action.payload instanceof ApiError
            ? action.payload
            : new ApiError({
                message: action.error?.message ?? 'Import failed',
                status: 0,
              });
        state.importReport = null;
      });
  },
});

export const {
  setSelectedTaxonomyIds,
  clearSelection,
  resetMutation,
  resetImport,
} = adminTaxonomiesSlice.actions;

export const adminTaxonomiesReducer = adminTaxonomiesSlice.reducer;

// ---------------------------------------------------------------------------
// Selectors
// ---------------------------------------------------------------------------

export interface AdminTaxonomiesRootStateSlice {
  adminTaxonomies: AdminTaxonomiesState;
}

export const selectAdminTaxonomySelectedIds = (
  state: AdminTaxonomiesRootStateSlice,
): number[] => state.adminTaxonomies.selectedTaxonomyIds;

export const selectAdminTaxonomyMutation = (
  state: AdminTaxonomiesRootStateSlice,
): { status: AdminTaxonomyMutationStatus; error: ApiError | null } => ({
  status: state.adminTaxonomies.mutationStatus,
  error: state.adminTaxonomies.mutationError,
});

export const selectAdminTaxonomyImport = (
  state: AdminTaxonomiesRootStateSlice,
): { status: AdminTaxonomyMutationStatus; error: ApiError | null; report: TaxonomyImportReport | null } => ({
  status: state.adminTaxonomies.importStatus,
  error: state.adminTaxonomies.importError,
  report: state.adminTaxonomies.importReport,
});
